import { FC, ReactNode, useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import axios, { CancelTokenSource } from 'axios';
import { isEqual } from 'lodash';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { selectCapacityFilters } from 'state/Capacities/Filters/filtersSlice';
import { CapacityCancellationContext } from './CapacityCancellationContext';

interface Props {
  children: ReactNode;
}

const CapacityCancellationProvider: FC<Props> = ({ children }) => {
  const { activeProjectsViewInTeamsCapacity = false } = useFlags();
  const sourceRef = useRef<CancelTokenSource | null>(null);

  // Keep the latest filters in a ref so `cancelInFlightRequests` can read them
  // without changing its identity (the refetch hooks list it as an effect
  // dependency, so an unstable callback would retrigger them on every render).
  const filters = useSelector(selectCapacityFilters);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  // Tracks the filters value the in-flight requests were last cancelled for, so
  // multiple hooks reacting to the same filter change cancel the shared source
  // only once. Without this, the second hook to run would cancel the requests
  // the first hook just started on the freshly-created source.
  const lastCancelledFiltersRef = useRef(filters);

  const ensureSource = useCallback(() => {
    if (!activeProjectsViewInTeamsCapacity) return null;
    if (!sourceRef.current) {
      sourceRef.current = axios.CancelToken.source();
    }
    return sourceRef.current;
  }, [activeProjectsViewInTeamsCapacity]);

  const getCancelToken = useCallback(
    () => ensureSource()?.token,
    [ensureSource]
  );

  const cancelInFlightRequests = useCallback(() => {
    if (!activeProjectsViewInTeamsCapacity) return;
    if (isEqual(lastCancelledFiltersRef.current, filtersRef.current)) {
      return;
    }
    lastCancelledFiltersRef.current = filtersRef.current;
    sourceRef.current?.cancel('Capacity filters changed');
    sourceRef.current = axios.CancelToken.source();
  }, [activeProjectsViewInTeamsCapacity]);

  useEffect(() => {
    return () => {
      sourceRef.current?.cancel('Capacity table unmounted');
    };
  }, []);

  return (
    <CapacityCancellationContext.Provider
      value={{ getCancelToken, cancelInFlightRequests }}
    >
      {children}
    </CapacityCancellationContext.Provider>
  );
};

export default CapacityCancellationProvider;
